'use client';

import { useState, useEffect } from 'react';
import type { RadarBlip, RadarQuadrant, RadarRing } from '@/lib/tech-radar/types';
import { RING_CONFIG, QUADRANT_CONFIG, generateBlipId } from '@/lib/tech-radar/types';

interface BlipEditorProps {
  blip: RadarBlip | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (blip: RadarBlip) => void;
  onDelete?: (id: string) => void;
  isNew?: boolean;
}

export function BlipEditor({
  blip,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isNew = false,
}: BlipEditorProps) {
  const [formData, setFormData] = useState<RadarBlip>({
    id: '',
    name: '',
    quadrant: 'techniques',
    ring: 'assess',
    description: '',
    rationale: '',
    isNew: false,
    tags: [],
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

   
  useEffect(() => {
    if (isOpen) {
      if (blip) {
        setFormData(blip);
      } else {
        setFormData({
          id: generateBlipId(),
          name: '',
          quadrant: 'techniques',
          ring: 'assess',
          description: '',
          rationale: '',
          isNew: true,
          tags: [],
        });
      }
      setErrors({});
      setTagInput('');
    }
  }, [blip, isOpen]);
   

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-background border border-border rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-muted">
          <h3 className="text-lg font-semibold text-foreground">
            {isNew ? 'Add Technology' : 'Edit Technology'}
          </h3>
          <button onClick={onClose} className="p-1 text-foreground-tertiary hover:text-foreground transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-1">Technology Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., React, Kubernetes, Python"
              className={`w-full px-3 py-2 bg-background/60 border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 ${
                errors.name ? 'border-red-500' : 'border-border'
              }`}
            />
            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Quadrant */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">Quadrant</label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(QUADRANT_CONFIG) as RadarQuadrant[]).map((q) => {
                const config = QUADRANT_CONFIG[q];
                return (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setFormData({ ...formData, quadrant: q })}
                    className={`p-3 text-left rounded-lg border transition-colors ${
                      formData.quadrant === q
                        ? 'border-accent bg-accent-muted'
                        : 'border-border-muted hover:border-border'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{config.icon}</span>
                      <span className="text-sm font-medium text-foreground">{config.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Ring */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">Ring</label>
            <div className="space-y-2">
              {(Object.keys(RING_CONFIG) as RadarRing[])
                .sort((a, b) => RING_CONFIG[a].order - RING_CONFIG[b].order)
                .map((r) => {
                  const config = RING_CONFIG[r];
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setFormData({ ...formData, ring: r })}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        formData.ring === r ? 'border-accent bg-accent-muted' : 'border-border-muted hover:border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                        <span className="font-medium text-foreground">{config.label}</span>
                      </div>
                      <div className="text-xs text-foreground-tertiary mt-1 ml-5">{config.description}</div>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-1">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this technology..."
              rows={2}
              className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          </div>

          {/* Rationale */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-1">Rationale</label>
            <textarea
              value={formData.rationale || ''}
              onChange={(e) => setFormData({ ...formData, rationale: e.target.value })}
              placeholder="Why is this technology in this ring?"
              rows={2}
              className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-1">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add tag..."
                className="flex-1 px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 text-sm"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-3 py-2 bg-cream/10 text-foreground rounded-lg hover:bg-cream/20 transition-colors"
              >
                Add
              </button>
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-cream/10 text-foreground-secondary rounded text-xs flex items-center gap-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-foreground-tertiary hover:text-foreground"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Flags */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isNew || false}
                onChange={(e) => setFormData({ ...formData, isNew: e.target.checked })}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground-secondary">Mark as new</span>
            </label>
          </div>

          {/* Previous ring (for movement indicator) */}
          {!isNew && (
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-1">Moved from</label>
              <select
                value={formData.movedFrom || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    movedFrom: e.target.value ? (e.target.value as RadarRing) : undefined,
                  })
                }
                className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
              >
                <option value="">No movement</option>
                {(Object.keys(RING_CONFIG) as RadarRing[]).map((r) => (
                  <option key={r} value={r}>
                    {RING_CONFIG[r].label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-border-muted">
            <div>
              {!isNew && onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Delete this technology?')) {
                      onDelete(formData.id);
                    }
                  }}
                  className="px-3 py-2 text-sm text-red-400 hover:text-red-300 transition-colors"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-foreground-tertiary hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                {isNew ? 'Add Technology' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
